import { Link } from "wouter";
import { MarketingNav, MarketingFooter } from "./layout";

export default function MarketingAbout() {
  return (
    <div
      className="min-h-screen flex flex-col bg-[hsl(var(--paper))] text-[hsl(var(--ink))]"
      data-testid="page-marketing-about"
    >
      <MarketingNav />

      <main className="flex-1">
        <div className="max-w-[760px] mx-auto px-6 md:px-12">

          <section className="pt-20 pb-0" data-testid="section-about-hero">
            <p className="text-[13px] tracking-[0.1em] uppercase text-[hsl(var(--graphite))] mb-6 font-mono">
              About
            </p>
            <h1
              className="text-[40px] md:text-[64px] font-medium leading-[1.02] tracking-[-0.04em] mb-7"
              data-testid="heading-about-hero"
            >
              I built the tool I couldn't afford to keep losing money without.
            </h1>
            <p className="text-[18px] md:text-[21px] leading-[1.5] text-[hsl(var(--ink-soft))] max-w-[620px]">
              Clarik wasn't dreamed up in a boardroom. It was built by a working
              entertainer who watched tens of thousands of dollars vanish into
              unpaid invoices — and then spent years learning exactly how to stop it.
            </p>
          </section>

          <section className="pt-14 space-y-6" data-testid="section-about-body">
            <p className="text-[17px] leading-[1.7] text-[hsl(var(--ink-soft))]">
              I spent years grinding in the entertainment industry. Booking my own
              shows, chasing my own leads, sending my own invoices. It was the life
              I wanted, and it was barely paying for itself.
            </p>

            <p className="text-[17px] leading-[1.7] text-[hsl(var(--ink-soft))]">
              When COVID shut the live business down, I pivoted out of necessity —
              into cybersecurity and IT for a financial services firm managing{" "}
              <strong className="text-[hsl(var(--ink))] font-medium">
                $4.2 billion in assets
              </strong>
              . I ended up working directly under the COO, Craig Morningstar at
              Dynamic Advisor Solutions. That chapter changed everything. I learned
              how real businesses run: data migrations, CRM infrastructure, the
              operational machinery that keeps a serious company from leaking money.
              I didn't know it yet, but I was being handed the exact toolkit I'd
              need for the rest of my life.
            </p>

            <p className="text-[17px] leading-[1.7] text-[hsl(var(--ink-soft))]">
              So I went back to entertainment — this time running it like an
              operation. I built my whole business on spreadsheets and grit. And it{" "}
              <em>worked</em>, on paper. But the cracks showed fast.
            </p>

            <p className="text-[17px] leading-[1.7] text-[hsl(var(--ink-soft))]">
              My sales reps were spending{" "}
              <strong className="text-[hsl(var(--ink))] font-medium">
                over an hour every night
              </strong>{" "}
              just drafting invoices, sending confirmation emails, and adding shows
              to my calendar. Hours more hunting for leads. Our lead lists became
              unmanageable. The reps never felt confident running mass email
              campaigns, and we never found a drip solution that fit. I flipped the
              model — lower-paid shows at higher frequency — and it filled the
              calendar.
            </p>

            <p className="text-[17px] leading-[1.7] text-[hsl(var(--ink-soft))]">
              Then the real problem hit. Even though we sent every invoice the
              moment a show booked, they got lost. Clients forgot to pay. Invoices
              disappeared into corporate AP departments and never came back.
            </p>

            <div
              className="my-12 py-8 border-t border-b border-[hsl(var(--fog))]"
              data-testid="block-about-pull-stat"
            >
              <p className="font-serif italic text-[26px] md:text-[34px] leading-[1.3] tracking-[-0.015em] text-[hsl(var(--ink))]">
                We had nearly{" "}
                <span className="not-italic font-medium font-sans text-[hsl(var(--green))]">
                  20% of our accounts unpaid
                </span>{" "}
                — tens of thousands of dollars in arrears. The business was
                working, and I still couldn't reliably cover my lodging or, some
                months, pay my own reps.
              </p>
            </div>

            <p className="text-[17px] leading-[1.7] text-[hsl(var(--ink-soft))]">
              That's the moment Clarik was born. Not as a startup idea — as a
              survival tool. I took everything I'd learned standing next to a COO
              at a multi-billion-dollar firm and pointed it at the problem I knew in
              my bones:{" "}
              <strong className="text-[hsl(var(--ink))] font-medium">
                the booking business loses money in the gaps between tools.
              </strong>
            </p>

            <p className="text-[17px] leading-[1.7] text-[hsl(var(--ink-soft))]">
              So I built one platform that closes every gap. It finds the leads. It
              powers the prospecting that books the shows. It generates invoices and
              calendar events automatically the moment a booking lands. It chases
              the late payments so you don't have to. It handles the accounting so
              tax season stops being a nightmare. And it keeps your availability and
              your book in one place.
            </p>

            <p className="text-[17px] leading-[1.7] text-[hsl(var(--ink-soft))]">
              I built it for myself first. Here's what happened to my own numbers:
            </p>

            <div
              className="grid grid-cols-1 md:grid-cols-3 gap-6 my-12"
              data-testid="block-about-growth"
            >
              <div>
                <p className="text-[11px] font-mono text-[hsl(var(--graphite))] tracking-[0.08em] mb-2">
                  2024 · SPREADSHEETS
                </p>
                <p className="text-[38px] font-medium tracking-[-0.03em] leading-none mb-1">
                  $45K
                </p>
                <p className="text-[13px] text-[hsl(var(--graphite))] leading-[1.45]">
                  Eight tools, unpaid invoices, late nights.
                </p>
              </div>
              <div>
                <p className="text-[11px] font-mono text-[hsl(var(--graphite))] tracking-[0.08em] mb-2">
                  2025 · BUILDING CLARIK
                </p>
                <p className="text-[38px] font-medium tracking-[-0.03em] leading-none mb-1 text-[hsl(var(--green))]">
                  $145K
                </p>
                <p className="text-[13px] text-[hsl(var(--graphite))] leading-[1.45]">
                  Ran the business on the thing I was building.
                </p>
              </div>
              <div>
                <p className="text-[11px] font-mono text-[hsl(var(--graphite))] tracking-[0.08em] mb-2">
                  2026 · THROUGH MAY
                </p>
                <p className="text-[38px] font-medium tracking-[-0.03em] leading-none mb-1 text-[hsl(var(--green))]">
                  $100K
                </p>
                <p className="text-[13px] text-[hsl(var(--graphite))] leading-[1.45]">
                  Already — and we're not through May yet.
                </p>
              </div>
            </div>

            <p className="text-[17px] leading-[1.7] text-[hsl(var(--ink-soft))]">
              Today Clarik runs solo talent booking their first season and it's
              built to scale to agencies coordinating hundreds of acts. It's
              intentionally opinionated: the entire workflow lives in one place,
              with strict workspace isolation and per-feature permissions. It's the
              senior tech stack I wish I'd had the day I sent my first invoice into
              the void.
            </p>

            <h2
              className="text-[28px] font-medium tracking-[-0.025em] mt-14 mb-6"
              data-testid="heading-about-beliefs"
            >
              What I believe
            </h2>

            <div className="space-y-6">
              <div className="pl-5 border-l-2 border-[hsl(var(--green))]" data-testid="belief-0">
                <p className="text-[17px] leading-[1.6] text-[hsl(var(--ink-soft))]">
                  <strong className="text-[hsl(var(--ink))] font-medium">
                    Talent and agencies should keep more of every booking.
                  </strong>{" "}
                  That's why Starter is free and the platform fee disappears the
                  moment you upgrade.
                </p>
              </div>
              <div className="pl-5 border-l-2 border-[hsl(var(--green))]" data-testid="belief-1">
                <p className="text-[17px] leading-[1.6] text-[hsl(var(--ink-soft))]">
                  <strong className="text-[hsl(var(--ink))] font-medium">
                    The tools should do the busywork.
                  </strong>{" "}
                  No one should spend an hour a night on invoices and confirmation
                  emails. That hour is why I built this.
                </p>
              </div>
              <div className="pl-5 border-l-2 border-[hsl(var(--green))]" data-testid="belief-2">
                <p className="text-[17px] leading-[1.6] text-[hsl(var(--ink-soft))]">
                  <strong className="text-[hsl(var(--ink))] font-medium">
                    You should never lose money in the gaps.
                  </strong>{" "}
                  An invoice sent is not an invoice paid — so the system should
                  chase it until it is.
                </p>
              </div>
              <div className="pl-5 border-l-2 border-[hsl(var(--green))]" data-testid="belief-3">
                <p className="text-[17px] leading-[1.6] text-[hsl(var(--ink-soft))]">
                  <strong className="text-[hsl(var(--ink))] font-medium">
                    Your data belongs to you.
                  </strong>{" "}
                  One-click ZIP export, anytime. No hostage situation.
                </p>
              </div>
            </div>

            <div
              className="mt-14 mb-14 p-7 bg-[hsl(var(--paper-soft,var(--paper)))] border border-[hsl(var(--fog))] rounded-2xl flex items-center gap-4"
              data-testid="block-about-signature"
            >
              <div className="w-[52px] h-[52px] rounded-full bg-[hsl(var(--green))] text-[hsl(var(--paper))] flex items-center justify-center font-medium text-base flex-shrink-0">
                JF
              </div>
              <div>
                <p className="font-medium text-[16px]">Josh Farley</p>
                <p className="text-[14px] text-[hsl(var(--graphite))]">
                  Founder, Clarik · still booking shows
                </p>
              </div>
            </div>

            <h2
              className="text-[28px] font-medium tracking-[-0.025em] mt-14 mb-4"
              data-testid="heading-about-talk"
            >
              Talk to me
            </h2>
            <p className="text-[17px] leading-[1.6] text-[hsl(var(--ink-soft))]">
              I read every message myself. Email{" "}
              <a
                href="mailto:admin@clarik.io"
                className="text-[hsl(var(--green))] underline underline-offset-2"
                data-testid="link-about-email"
              >
                admin@clarik.io
              </a>{" "}
              or apply for early access below. No demo gate, no sales call —
              founding spots are limited to 10 per business type, and I'm
              onboarding them by hand.
            </p>

            <div className="mt-10 mb-20" data-testid="block-about-cta">
              <Link
                href="/beta"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[hsl(var(--green))] text-[hsl(var(--paper))] rounded-full text-[15px] font-medium hover:opacity-90 transition-opacity"
                data-testid="button-about-request-access"
              >
                Request access →
              </Link>
            </div>
          </section>

        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}
